
import _ from "lodash";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import HealthInformation from "./Steps/ClientProfileSteps/HealthInformation.vue";
import SupportNetwork from "./Steps/ClientProfileSteps/SupportNetwork.vue";
import CareGoals from "./Steps/ClientProfileSteps/CareGoals.vue";
import AdditionalNotes from "./Steps/ClientProfileSteps/AdditionalNotes.vue";
import UIButton from "@/components/button/ui-button.vue";
import ClientInformation from "./Steps/CarePlanSteps/ClientInformation.vue";
import RiskAlert from "./Steps/CarePlanSteps/RiskAlert.vue";
import ClientServices from "./Steps/CarePlanSteps/ClientServices.vue";
import Goals from "./Steps/CarePlanSteps/Goals.vue";
import Interventions from "./Steps/CarePlanSteps/Interventions.vue";
import FunctionalAssessment from "./Steps/CarePlanSteps/FunctionalAssessment.vue";
import HomeEnvironment from "./Steps/CarePlanSteps/HomeEnvironment.vue";
import PersonalConsiderations from "./Steps/CarePlanSteps/PersonalConsiderations.vue";
import DocumentationRequirements from "./Steps/CarePlanSteps/DocumentationRequirements.vue";
import { User } from "@/generated/graphql";

@Component({
  components: {
    ClientInformation,
    RiskAlert,
    ClientServices,
    HealthInformation,
    SupportNetwork,
    CareGoals,
    AdditionalNotes,
    Goals,
    Interventions,
    FunctionalAssessment,
    HomeEnvironment,
    PersonalConsiderations,
    DocumentationRequirements,
    UIButton,
  },
})
export default class CarePlanTab extends Vue {
  @Prop({ type: Object, required: true }) readonly formData!: Record<
    string,
    any
  >;
  @Prop({ type: Number, required: true }) readonly initialStep!: number;
  @Prop({ type: Boolean, default: "null" }) readonly isDrafted!: boolean;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: String, default: true }) readonly clientId!: boolean;

  @Prop({ type: Object }) readonly invalid: any;

  currentStep = 0;
  visibleRange = { start: 0, end: 4 };
  highestCompletedStep: number = -1;
  scrollOffset = 0;
  steps = [
    { title: "Client Information" },
    { title: "Risks and Alerts" },
    { title: "Client Services" },
    { title: "Goals" },
    { title: "Interventions" },
    { title: "Functional Assessment" },
    { title: "Home Environment" },
    { title: "Personal Considerations" },
    { title: "Documentation Requirements" },
  ];
  localFormData: Record<string, any> = {};

  created() {
    this.localFormData = _.cloneDeep(this.formData);
    this.setInitialStep();
  }

  setInitialStep() {
    this.currentStep = this.initialStep;
    if (this.initialStep >= this.visibleRange.end - 1) {
      const newStart = Math.max(0, this.initialStep - 2);
      this.visibleRange = {
        start: newStart,
        end: newStart + 4,
      };
    }
  }

  @Watch("initialStep")
  onInitialStepChange(newStep: number) {
    if (newStep !== this.currentStep) {
      this.currentStep = newStep;
      this.$nextTick(() => {
        this.centerCurrentStep();
      });
    }
  }

  private debouncedUpdate = _.debounce((newData: Record<string, any>) => {
    this.$emit("update:formData", newData);
  }, 300);
  updateField(field: string, value: any) {
    this.localFormData[field] = value;
    this.$emit("update:formData", this.localFormData);
  }
  handleFormDataUpdate(updatedData: Record<string, any>) {
    this.localFormData = { ...this.localFormData, ...updatedData };

    this.debouncedUpdate(this.localFormData);
  }
  handleSaveDraft() {
    const cleanedData = _.omit(this.localFormData, ["__typename"]);
    this.$emit("save-draft", cleanedData);
  }
  saveDraft() {
    this.handleSaveDraft();
  }
  async submit() {
    try {
      const cleanedData = _.omit(this.localFormData, ["__typename"]);
      await this.$emit("submit", cleanedData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }
  get isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  isStepComplete(index) {
    return index < this.currentStep;
  }

  handleNext() {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      if (this.currentStep > this.highestCompletedStep) {
        this.highestCompletedStep = this.currentStep - 1;
      }
      if (
        this.currentStep === this.visibleRange.end - 1 &&
        this.currentStep < this.steps.length - 2
      ) {
        this.visibleRange = {
          start: this.visibleRange.start + 1,
          end: this.visibleRange.end + 1,
        };
      }
    } else {
      this.$emit("submit", this.localFormData);
    }
  }

  handlePrev(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      if (this.currentStep === this.visibleRange.start + 1) {
        this.visibleRange = {
          start: this.visibleRange.start - 1,
          end: this.visibleRange.end - 1,
        };
      }
    }
  }

  @Watch("currentStep")
  onStepChange() {
    this.$nextTick(() => {
      this.centerCurrentStep();
    });
  }

  centerCurrentStep() {
    const container = this.$refs.progressContainer as HTMLElement;
    const currentStepEl = this.$refs.currentStep as HTMLElement[];

    if (container && currentStepEl && currentStepEl[0]) {
      const containerWidth = container.offsetWidth;
      const stepWidth = currentStepEl[0].offsetWidth;
      const stepLeft = currentStepEl[0].offsetLeft;

      this.scrollOffset = -(stepLeft - containerWidth / 2 + stepWidth / 2);

      // Prevent overscrolling at the start
      const maxScroll = 0;
      // Prevent overscrolling at the end
      const minScroll = -(container.scrollWidth - containerWidth);

      this.scrollOffset = Math.min(
        maxScroll,
        Math.max(minScroll, this.scrollOffset)
      );
    }
  }

  mounted() {
    this.centerCurrentStep();
  }
}
